import { useEffect, useRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { DrawApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { InspectionStatusType, InspectionType } from '@apply/constants';
import { getSelectableFiles } from './functions/functions';
import { useInspectionsModalStore, canOrderStatusInspection, canOrderDrawInspection } from './store/useInspectionsModalStore';
import {
    useInspectionsStateQuery,
    useCreateInspectionMutation,
    useRefreshInspectionMutation,
    useResetErrorMutation,
    useLinkToDrawMutation,
} from './queries/inspectionsQueries';
import InspectionsGrid from './components/InspectionsGrid';
import NewInspectionForm from './components/NewInspectionForm';
import InspectionDetails from './components/InspectionDetails';

const drawApi = new DrawApi.DrawApi(applicationInsights);

interface InspectionsModalProps {
    isOpen: boolean;
    drawBudgetId: number;
    set_drawBudget?: (drawBudget: unknown) => void;
    onClose: () => void;
}

const InspectionsModal = ({ isOpen, drawBudgetId, set_drawBudget, onClose }: InspectionsModalProps) => {
    const {
        currentInspection,
        setCurrentInspection,
        isNewInspection,
        setIsNewInspection,
        drawId,
        setDrawId,
        inspectionType,
        setInspectionType,
        setFileIds,
        setDrawBudgetId,
        reset,
    } = useInspectionsModalStore();

    const { data: inspectionsData, isFetching, refetch } = useInspectionsStateQuery();

    const createMutation = useCreateInspectionMutation(drawBudgetId);
    const refreshMutation = useRefreshInspectionMutation();
    const resetErrorMutation = useResetErrorMutation(drawBudgetId);
    const linkToDrawMutation = useLinkToDrawMutation(drawBudgetId);

    const loadingOpen =
        isFetching ||
        createMutation.isPending ||
        refreshMutation.isPending ||
        resetErrorMutation.isPending ||
        linkToDrawMutation.isPending;

    const loadingMessage = createMutation.isPending
        ? 'Ordering...'
        : resetErrorMutation.isPending
          ? 'Requesting Error reset...'
          : linkToDrawMutation.isPending
            ? 'Linking to Draw...'
            : refreshMutation.isPending
              ? 'Requesting refresh...'
              : 'Loading...';

    const scrollableContentRef = useRef<HTMLDivElement>(null);

    const styles = {
        buttonContainer: {
            textAlign: 'right' as const,
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
    };

    useEffect(() => {
        if (isOpen) {
            setDrawBudgetId(drawBudgetId);
        } else {
            reset();
        }
    }, [isOpen, drawBudgetId]);

    useEffect(() => {
        if (!inspectionsData) return;
        if (inspectionsData.inspections.length > 0) {
            setCurrentInspection(inspectionsData.inspections[0]);
        }
    }, [inspectionsData]);

    useEffect(() => {
        if (inspectionType === InspectionType.Draw && inspectionsData) {
            setDrawId(null);
            const drawInspections = inspectionsData.inspections.filter((i) => i.inspectionTypeId === InspectionType.Draw);
            inspectionsData.draws.forEach((d) => {
                if (!drawInspections.find((i) => i.drawId === d.id)) {
                    setDrawId(d.id);
                }
            });
        }
    }, [inspectionType]);

    const isOrderNewDisabled = (): boolean => {
        if (!inspectionsData || isNewInspection) return true;
        if (!inspectionsData.inspections.every((i) => i.statusId === InspectionStatusType.InspectionDelivered)) return true;
        if (canOrderStatusInspection(inspectionsData ?? null)) return false;
        return !canOrderDrawInspection(inspectionsData ?? null);
    };

    const onOrderNewInspection = () => {
        setCurrentInspection(null);
        setDrawId(null);

        if (canOrderDrawInspection(inspectionsData ?? null)) {
            setInspectionType(InspectionType.Draw);
            const drawInspections = inspectionsData!.inspections.filter((i) => i.inspectionTypeId === InspectionType.Draw);
            inspectionsData!.draws.forEach((d) => {
                if (!drawInspections.find((i) => i.drawId === d.id)) setDrawId(d.id);
            });
        } else if (canOrderStatusInspection(inspectionsData ?? null)) {
            setInspectionType(InspectionType.Status);
        }
        setFileIds(getSelectableFiles(inspectionsData!.files).map((f) => f.id));
        setIsNewInspection(true);
    };

    const cancelStatusOrder = () => {
        setIsNewInspection(false);
        setCurrentInspection(null);
    };

    const onSubmitNewOrder = () => {
        createMutation.mutate({
            drawBudgetId,
            drawId: inspectionType === InspectionType.Draw ? (drawId as number) : null,
            fileIds: useInspectionsModalStore.getState().fileIds,
        });
    };

    const onRefreshStatus = () => {
        if (currentInspection) {
            refreshMutation.mutate(currentInspection.id);
        }
    };

    const onResetError = () => {
        if (currentInspection) {
            resetErrorMutation.mutate(currentInspection.id);
        }
    };

    const onLinkToDraw = async () => {
        if (!currentInspection) return;
        await linkToDrawMutation.mutateAsync({ inspectionId: currentInspection.id, drawId: drawId as number });
        const drawBudgetResponse = await drawApi.getDrawBudget(drawBudgetId);
        set_drawBudget?.(drawBudgetResponse.result);
    };

    const onReloadModal = async () => {
        const inspId = currentInspection?.id;
        setCurrentInspection(null);
        setDrawId(null);
        const result = await refetch();
        if (inspId && result.data) {
            const insp = result.data.inspections.find((i) => i.id === inspId);
            if (insp) setCurrentInspection(insp);
        }
    };

    if (!isOpen || !inspectionsData) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={onClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex={-1}
                        role='dialog'
                        style={{ width: '900px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>Inspections</h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={onClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                            <div
                                className='main-content'
                                style={{ padding: '0', width: '100%' }}
                            >
                                <div
                                    className='scrollable-content'
                                    ref={scrollableContentRef}
                                >
                                    <div className='standard-form'>
                                        <InspectionsGrid
                                            scrollableContentRef={scrollableContentRef}
                                            isOrderNewDisabled={isOrderNewDisabled()}
                                            onOrderNewInspection={onOrderNewInspection}
                                        />
                                        {isNewInspection && (
                                            <NewInspectionForm
                                                onSubmit={onSubmitNewOrder}
                                                onCancel={cancelStatusOrder}
                                            />
                                        )}
                                        {currentInspection && (
                                            <InspectionDetails
                                                onRefreshStatus={onRefreshStatus}
                                                onResetError={onResetError}
                                                onLinkToDraw={onLinkToDraw}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div
                                className='button-footer'
                                style={styles.buttonContainer}
                            >
                                <button
                                    type='button'
                                    className='button primary'
                                    data-dismiss='modal'
                                    onClick={onClose}
                                    title='Close this window'
                                >
                                    Close
                                </button>
                                &nbsp;
                                <button
                                    type='button'
                                    className='button hollow'
                                    data-dismiss='modal'
                                    onClick={onReloadModal}
                                    title='Reload this modal and refresh data'
                                >
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default InspectionsModal;
